import { GoogleOAuthProvider } from '@react-oauth/google';
import mixpanel from "mixpanel-browser";
import ReactGA from "react-ga4"
import ReactDOM from "react-dom/client";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import * as Sentry from "@sentry/react";
import { installAuthInterceptor } from "./utils/authInterceptor";
import "./index.css";

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  enabled: import.meta.env.VITE_APP_ENV !== "development" ? true : false,
  environment: import.meta.env.VITE_APP_ENV,
  sendDefaultPii: true,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      beforeAddRecordingEvent: (event) => {
        const path = window.location.pathname;
        const replayPages = [
          /^\/screening-interview(\/|$)/,
          /^\/assessment-interview(\/|$)/,
          /^\/jobs\/[^/]+\/applications(\/|$)/,
          /^\/billing(\/|$)/, // NOTE: PII scrubbing/masking must be configured in Sentry for this route
        ];
        if (!replayPages.some((re) => re.test(path))) return null;
        return event;
      },
    }),
  ],
  tracesSampleRate: 0.3,
  // Session replay rate is 0; recording is controlled by beforeAddRecordingEvent page filter above
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 0.4,
  beforeSend(event) {
    const statusCode =
      event.contexts?.response?.status_code ??
      event.extra?.status_code;

    if (statusCode === 404) return null;

    const message =
      event.message ||
      event.exception?.values?.[0]?.value ||
      "";

    if (
      message.includes("404") ||
      message.includes("Not Found") ||
      message.includes("not found")
    ) {
      return null;
    }

    return event;
  },
});

ReactGA.initialize([
  {
    trackingId: "G-HP4W0Y7QRJ",
    gaOptions: {
      siteSpeedSampleRate: 100,
      alwaysSendReferrer: true,
    },
    gtagOptions: {
      testMode: import.meta.env.VITE_APP_ENV !== "production",
      send_page_view: false,
    },
  },
  {
    trackingId: "G-EHGBPDY290",
    gaOptions: {
      siteSpeedSampleRate: 100,
      alwaysSendReferrer: true,
    },
    gtagOptions: {
      testMode: import.meta.env.VITE_APP_ENV !== "production",
      send_page_view: false,
    },
  }
]);

if (import.meta.env.VITE_APP_ENV === "production") {
  mixpanel.init(import.meta.env.VITE_MIXPANEL_TOKEN, {
    autocapture: false,
    debug: true,
    track_pageview: false,
    persistence: "localStorage",
  })
}

// Before anything renders, so the very first request of the session is covered.
// Without this a 401 fell through to whatever error each screen happened to
// render, and an expired session looked like a broken feature instead of a
// sign-out. See src/utils/authInterceptor.ts.
installAuthInterceptor();

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);
root.render(
  <GoogleOAuthProvider clientId={import.meta.env.VITE_GOOGLE_CLIENT_ID || ''}>
    <App />
  </GoogleOAuthProvider>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
